/**
 * Programs management page.
 * List with {@link SlidePanel} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { SlidePanel } from '@/components/common/SlidePanel';
import { ProgramsDataTable } from '@/components/programs/ProgramsDataTable';
import { CreateProgramView } from '@/components/programs/CreateProgramView';
import { EditProgramView } from '@/components/programs/EditProgramView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';

/** Programs list with {@link SlidePanel} create/edit. */
export const ProgramsManagementPage: React.FC = () => {
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('programs');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editProgramId, setEditProgramId] = React.useState<string | null>(null);
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const { viewMode, setViewMode } = useViewMode('programs-management');

  /** Deep-link: open create/edit/view via `?create=` / `?edit=` / `?view=`. */
  React.useEffect(() => {
    const editId = searchParams.get('edit');
    const viewId = searchParams.get('view');
    const shouldCreate = searchParams.get('create') === 'true';

    if (editId) {
      setEditProgramId(editId);
      closeRow();
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }

    if (viewId) {
      setOpenRow(viewId);
      setCreateFormOpen(false);
      setEditProgramId(null);
    }

    if (shouldCreate) {
      setCreateFormOpen(true);
      setEditProgramId(null);
      closeRow();
    }
  }, [searchParams, setSearchParams, closeRow, openRow]);

  /** Open the edit panel and close any expanded details row. */
  const handleEditProgram = (id: string) => {
    setEditProgramId(id);
    closeRow();
  };

  return (
    <ManagementPage
      title="Gestion des Programmes"
      subtitle="Créez, modifiez et gérez tous vos programmes académiques"
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              setCreateFormOpen(true);
              setEditProgramId(null);
              closeRow();
            }}
          >
            Créer un programme
          </Button>
        ) : undefined
      }
    >
      <ProgramsDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditProgram={handleEditProgram}
        enableFilters={true}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateProgramView
          onClose={() => setCreateFormOpen(false)}
          onSuccess={() => {
            setCreateFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['programs'] });
          }}
        />
      </SlidePanel>

      <SlidePanel isOpen={!!editProgramId} onClose={() => setEditProgramId(null)}>
        {editProgramId && (
          <EditProgramView
            key={editProgramId}
            programId={editProgramId}
            onClose={() => setEditProgramId(null)}
            onSuccess={() => {
              setEditProgramId(null);
              void queryClient.invalidateQueries({ queryKey: ['programs'] });
            }}
          />
        )}
      </SlidePanel>
    </ManagementPage>
  );
};
